import React from "react";
import ReactDOM from "react-dom/client";
import "@/styles/keymade-tokens.css";
import "@/index.css";
import App from "@/App";
import { applySkin, applyType } from "@/utils/skin";

// DP92 Part A — the skin is resolved by ONE function, utils/skin.resolveSkin:
// dark mode ON => night, OFF => the firm's own skin, missing or unreadable =>
// studio. This block used to hold the rule itself (`dark ? 'night' : 'studio'`),
// which is why Gallery was measured, correct and unreachable for five passes.
// Skins are declared on <body> in keymade-tokens.css, so a converted card cannot
// choose its own — a light card on a dark desk would be wrong.
// Lives here rather than in App.js: adding this to App.js makes the platform's
// visual-edits babel plugin blow its stack (reported, not worked around).
(() => {
  applySkin();
  applyType();
  new MutationObserver(applySkin).observe(document.documentElement, {
    attributes: true, attributeFilter: ['class', 'data-km-daylight'],
  });
})();

// Jobsite Capture Arc Stage 2A (2026-06-15) — narrowly-scoped SW lifecycle.
// We deliberately preserve the anti-stale-build stance: kill any service
// worker registered from a previous build whose scriptURL is NOT exactly
// our `/sw.js`, then register our own. Our `/sw.js` has NO fetch handler
// and NO Cache API writes, so a fresh code change still ships on the next
// page load without a hard cache-clear (proven in stage2a tests).
if ('serviceWorker' in navigator) {
  const OUR_SW_PATH = '/sw.js';
  (async () => {
    try {
      const registrations = await navigator.serviceWorker.getRegistrations();
      for (const reg of registrations) {
        const url = reg.active?.scriptURL || reg.installing?.scriptURL || reg.waiting?.scriptURL || '';
        if (!url.endsWith(OUR_SW_PATH)) {
          await reg.unregister();
        }
      }
      // Wave 5.9 Build 12 — the app-side SW now caches image responses
      // (see /sw.js). Preserve `keymade-images-*` caches on boot; wipe
      // anything else (defense-in-depth against a prior build that
      // accidentally created a shell/JS/CSS cache). To invalidate the
      // image cache on a deploy, bump `IMG_CACHE_VERSION` in /sw.js —
      // the SW's activate handler drops the old-version cache.
      if ('caches' in window) {
        const names = await caches.keys();
        await Promise.all(
          names
            .filter((n) => !n.startsWith('keymade-images-'))
            .map((n) => caches.delete(n))
        );
      }
      await navigator.serviceWorker.register(OUR_SW_PATH);
    } catch (_err) {
      // Service workers may be blocked (private mode / unsupported) —
      // the app must still boot. Page-driven upload drain handles the
      // offline-then-sync requirement without an SW.
    }
  })();
}

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
);
